import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../../api/client';
import { Alert, Badge, Button } from '../../../components/ui';
import { alreadyCommissioned } from '../RetireCommission';
import {
  assetTypeFor, pickParent, plannedMeters,
  type OnboardingReadiness, type OnboardingRequest, type ParentPick,
} from '../../../lib/onboarding';

type Parent = ParentPick | { kind: 'error'; message: string };
// A retry is always safe: finalize claims the serial before it creates anything, so a retry
// adopts an asset an earlier attempt created instead of making another. 'in_progress': another
// finalize holds the claim (or one that failed after creating the asset holds it for a minute);
// the meter stays pending and the next click retries it.
// `warnings`: what finalize reported on a commit (an adopted asset, a breaker size left as it was).
type Outcome = { state: 'running' | 'committed' | 'done' | 'failed' | 'in_progress'; detail?: string; warnings?: string[] };
type FinalizeError = Error & { status?: number; body?: { error?: string; code?: string; detail?: string; orphan_asset_id?: string } };

/** A finalize failure that every other meter in the realm would repeat: stop at the first. */
function realmWide(e: FinalizeError): boolean {
  return e.status === 403 || (e.status === 502 && e.body?.error === 'or_service_token_failed');
}

// Commissions a submitted request's planned meters through the existing finalize
// endpoint, one call per meter (no bulk endpoint), in the request's realm. Readiness
// is read live, so nothing has to tell this page that apply ran.
export default function CommissionPanel({ request }: { request: OnboardingRequest }) {
  const realm = request.realm;
  const meters = useMemo(() => plannedMeters(request.payload), [request.payload]);
  const gateways = useMemo(
    () => request.payload.sites.flatMap((site) => site.gateways.map((gateway) => ({ site, gateway }))),
    [request.payload],
  );
  const [readiness, setReadiness] = useState<OnboardingReadiness | null>(null);
  const [parents, setParents] = useState<Record<string, Parent>>({});
  const [outcomes, setOutcomes] = useState<Record<string, Outcome>>({});
  const [checking, setChecking] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const check = useCallback(async () => {
    setChecking(true);
    setError(null);
    try {
      const r = await api.getOnboardingReadiness(request.id, realm);
      const next: Record<string, Parent> = {};
      for (const { site, gateway } of gateways) {
        try {
          const found = await api.searchParentAssets(gateway.group, realm);
          next[gateway.serial] = pickParent(found.data, realm, site, gateway);
        } catch (e) {
          next[gateway.serial] = { kind: 'error', message: (e as Error).message };
        }
      }
      setReadiness(r.data);
      setParents(next);
    } catch (e) {
      // A failed check must not leave the last good state looking ready.
      setReadiness(null);
      setParents({});
      setError((e as Error).message);
    } finally {
      setChecking(false);
    }
  }, [request.id, realm, gateways]);

  useEffect(() => { void check(); }, [check]);

  const committed = new Set(readiness?.committed_serials ?? []);
  const isSettled = (serial: string) => {
    const state = outcomes[serial]?.state;
    return committed.has(serial) || state === 'committed' || state === 'done';
  };
  const pending = meters.filter(({ meter }) => !isSettled(meter.serial));

  const blockers: string[] = [];
  if (readiness && !readiness.or_service_account) {
    blockers.push(`billing-api has no OpenRemote service account for ${realm} yet (binding ${readiness.binding}). The operator sets it up after apply.`);
  }
  for (const { site, gateway } of gateways) {
    const p = parents[gateway.serial];
    if (p?.kind === 'missing') blockers.push(`Group "${gateway.group}" not found under ${site.city} > ${site.building} > ${gateway.room}. Run apply first.`);
    if (p?.kind === 'ambiguous') blockers.push(`Group "${gateway.group}" matches ${p.count} assets under ${site.building} > ${gateway.room}. Remove the duplicates in OpenRemote.`);
    if (p?.kind === 'error') blockers.push(`Could not look up group "${gateway.group}": ${p.message}`);
  }
  const ready = readiness !== null && gateways.every(({ gateway }) => parents[gateway.serial]) && blockers.length === 0;

  const run = async () => {
    setRunning(true);
    setError(null);
    let stopped = false;
    for (const { gateway, meter } of pending) {
      const parent = parents[gateway.serial];
      if (parent?.kind !== 'found') continue;
      const elec = gateway.utility === 'electricity';
      setOutcomes((o) => ({ ...o, [meter.serial]: { state: 'running' } }));
      try {
        const done = await api.finalizeCommissioning({
          meter_serial: meter.serial,
          asset_type: assetTypeFor(gateway.utility),
          parent_id: parent.asset_id,
          source: 'manual',
          source_payload: { onboarding_request_id: request.id, gateway: gateway.serial, ...(meter.make ? { make: meter.make } : {}) },
          ...(elec && meter.breaker_size_amps !== null ? { breaker_size_amps: meter.breaker_size_amps } : {}),
          ...(elec && meter.breaker_phases !== null ? { breaker_phases: meter.breaker_phases } : {}),
          ...(elec && meter.factor !== null ? { factor: meter.factor } : {}),
        }, realm);
        setOutcomes((o) => ({ ...o, [meter.serial]: { state: 'committed', warnings: done.warnings ?? [] } }));
      } catch (e) {
        if (alreadyCommissioned(e)) {
          // Another attempt committed it while this one ran, and this one's asset is left over: say which to retire.
          const orphan = (e as FinalizeError).body?.orphan_asset_id;
          setOutcomes((o) => ({ ...o, [meter.serial]: { state: 'done', detail: orphan ? (e as Error).message : 'Already commissioned' } }));
          continue;
        }
        const err = e as FinalizeError;
        const message = err.message;
        if (err.status === 409 && err.body?.code === 'commission_in_progress') {
          setOutcomes((o) => ({ ...o, [meter.serial]: { state: 'in_progress', detail: message } }));
          continue;
        }
        setOutcomes((o) => ({ ...o, [meter.serial]: { state: 'failed', detail: message } }));
        if (message === 'Session expired') {
          stopped = true;
          setError('Your session expired. Sign in again, then press Commission again; finished meters are kept.');
          break;
        }
        if (realmWide(err)) {
          stopped = true;
          const why = err.body?.detail ? `${message}: ${err.body.detail}` : message;
          setError(err.status === 403
            ? `Stopped: billing-api refused commissioning in ${realm} (${why}). Every meter would fail the same way. Tell Beau.`
            : `Stopped: billing-api could not sign in to OpenRemote for ${realm} (${why}). The realm's OpenRemote `
              + 'service account is missing or wrong, so every meter would fail the same way. Tell Beau.');
          break;
        }
      }
    }
    setRunning(false);
    // A re-check clears the alert that says why the run stopped (and after an expiry it would fail
    // with the same token), so it runs only after a run that finished.
    if (!stopped) void check();
  };

  const label = (serial: string) => {
    const o = outcomes[serial];
    if (committed.has(serial) || o?.state === 'committed') return { text: 'Commissioned', tone: 'success' as const };
    if (o?.state === 'done') return { text: 'Already commissioned', tone: 'success' as const };
    if (o?.state === 'running') return { text: 'Commissioning', tone: 'info' as const };
    if (o?.state === 'in_progress') return { text: 'In progress', tone: 'warning' as const };
    if (o?.state === 'failed') return { text: 'Failed', tone: 'danger' as const };
    return { text: 'Planned', tone: 'neutral' as const };
  };

  return (
    <section aria-labelledby="ob-commission" className="bg-surface-raised rounded-xl border border-border-token">
      <div className="flex items-center justify-between gap-3 px-4 lg:px-6 py-3 border-b border-border-token">
        <h2 id="ob-commission" className="text-base font-semibold text-content">Planned meters</h2>
        <Button variant="secondary" size="sm" disabled={checking || running} onClick={() => void check()}>Check again</Button>
      </div>
      <div className="px-4 lg:px-6 py-4 space-y-4">
        {error && <Alert tone="danger">{error}</Alert>}
        {meters.length === 0 && <p className="text-sm text-content-muted">This request plans no meters.</p>}
        {blockers.length > 0 && (
          <Alert tone="warning" title="Not ready to commission">
            <ul className="list-disc pl-5 space-y-1">{blockers.map((b) => <li key={b}>{b}</li>)}</ul>
          </Alert>
        )}
        <ul className="space-y-2">
          {meters.map(({ site, gateway, meter }) => {
            const l = label(meter.serial);
            const o = outcomes[meter.serial];
            return (
              <li key={meter.serial} className="flex items-start justify-between gap-3">
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-content">{meter.serial}</span>
                  <span className="block text-xs text-content-muted break-words">
                    {site.building} &gt; {gateway.room} &gt; {gateway.group}{meter.factor !== null ? `, factor ${meter.factor}` : ''}
                  </span>
                  {o?.detail && <span className="block text-xs text-content break-words">{o.detail}</span>}
                  {o?.warnings?.map((w, i) => <span key={`${i}:${w}`} className="block text-xs text-content-muted break-words">{w}</span>)}
                </span>
                <Badge tone={l.tone}>{l.text}</Badge>
              </li>
            );
          })}
        </ul>
        <Button disabled={!ready || checking || running || pending.length === 0} onClick={() => void run()}>
          {running ? 'Commissioning...' : `Commission planned meters (${pending.length})`}
        </Button>
      </div>
    </section>
  );
}
